import { BROWSER_PROTOCOL_VERSION } from './contracts';
import { WEBSOCKET_GENERATION_MISMATCH_CLOSE_CODE } from './WebSocketProtocol';

export type WorldRealtimeHello = Readonly<{
  villageId: number;
  streamGeneration: number;
  lastCursor: number;
  entityRevision: number;
}>;

export type WorldRealtimeTicket = Readonly<{
  ticket: string;
  realtimePath: string;
  subprotocol: 'nord-world-v2';
  sessionGeneration: number;
}>;

type Pending<T> = {
  resolve: (value: T) => void;
  reject: (reason?: unknown) => void;
  cleanup: () => void;
};

export class WorldRealtimeResponseError extends Error {
  override readonly name = 'WorldRealtimeResponseError';
  constructor(readonly response: unknown) {
    super('The realtime server rejected the frame.');
  }
}

export class WorldRealtimeVisitorKickError extends Error {
  override readonly name = 'WorldRealtimeVisitorKickError';

  constructor(
    readonly sourceVillageId: number,
    readonly destinationVillageId: number,
  ) {
    super('The village owner sent this player home.');
  }
}

export class WorldRealtimeClient {
  private socket?: WebSocket;
  private ready?: Promise<void>;
  private readyResolve: (() => void) | undefined;
  private readyReject: ((reason?: unknown) => void) | undefined;
  private readonly notifications: unknown[] = [];
  private notificationWaiter: Pending<unknown> | undefined;
  private readonly movements = new Map<number, Pending<unknown>>();
  private closed = false;
  private failureReason: Error | undefined;

  constructor(
    readonly token: string,
    readonly hello: WorldRealtimeHello,
    private readonly ticket: WorldRealtimeTicket,
  ) {}

  get isClosed(): boolean {
    return this.closed;
  }

  async connect(): Promise<void> {
    if (this.ready !== undefined) return this.ready;
    this.ready = new Promise<void>((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
    const url = new URL(this.ticket.realtimePath, globalThis.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('ticket', this.ticket.ticket);
    const socket = new WebSocket(url, this.ticket.subprotocol);
    this.socket = socket;
    socket.addEventListener('open', () => {
      socket.send(
        JSON.stringify({
          type: 'hello',
          ...this.hello,
          sessionGeneration: this.ticket.sessionGeneration,
        }),
      );
    });
    socket.addEventListener('message', (event) => this.receive(event.data));
    socket.addEventListener('error', () => this.fail(new Error('The realtime connection failed.')));
    socket.addEventListener('close', () => this.fail(new Error('The realtime connection closed.')));
    return this.ready;
  }

  async nextNotification(signal?: AbortSignal): Promise<unknown> {
    signal?.throwIfAborted();
    await this.connect();
    if (this.closed) {
      throw this.failureReason ?? new Error('The realtime connection is unavailable.');
    }
    const queued = this.notifications.shift();
    if (queued !== undefined) return queued;
    if (this.notificationWaiter !== undefined) {
      throw new Error('A notification read is already pending.');
    }
    return new Promise<unknown>((resolve, reject) => {
      const pending = this.pending(resolve, reject, signal);
      this.notificationWaiter = pending;
    });
  }

  async submitMovement(
    movement: Readonly<{
      sequence: number;
      posX: number;
      posY?: number;
      posZ: number;
      rotation: number;
      boostActive?: true;
    }>,
    signal?: AbortSignal,
  ): Promise<unknown> {
    signal?.throwIfAborted();
    await this.connect();
    if (this.closed || this.socket?.readyState !== WebSocket.OPEN) {
      throw new Error('The realtime connection is unavailable.');
    }
    return new Promise<unknown>((resolve, reject) => {
      const pending = this.pending(resolve, reject, signal);
      this.movements.set(movement.sequence, pending);
      this.socket?.send(
        JSON.stringify({
          type: 'movement',
          streamGeneration: this.hello.streamGeneration,
          sessionGeneration: this.ticket.sessionGeneration,
          ...movement,
        }),
      );
    });
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.socket?.close(1000, 'world changed');
    this.fail(new Error('The realtime connection was replaced.'));
  }

  private pending<T>(
    resolve: (value: T) => void,
    reject: (reason?: unknown) => void,
    signal?: AbortSignal,
  ): Pending<T> {
    const aborted = (): void => reject(signal?.reason);
    signal?.addEventListener('abort', aborted, { once: true });
    return {
      resolve,
      reject,
      cleanup: () => signal?.removeEventListener('abort', aborted),
    };
  }

  private receive(raw: unknown): void {
    if (typeof raw !== 'string') {
      this.fail(new Error('The realtime server sent a non-JSON frame.'));
      return;
    }
    let frame: unknown;
    try {
      frame = JSON.parse(raw) as unknown;
    } catch (error) {
      this.fail(error);
      return;
    }
    if (!isRecord(frame) || typeof frame.type !== 'string') {
      this.fail(new Error('The realtime server sent an invalid frame.'));
      return;
    }
    if (
      (frame.type === 'ready' ||
        frame.type === 'notification' ||
        frame.type === 'movement-ack' ||
        frame.type === 'movement-error' ||
        frame.type === 'resync-required' ||
        frame.type === 'village-handoff' ||
        frame.type === 'server-closing') &&
      (frame.streamGeneration !== this.hello.streamGeneration ||
        frame.sessionGeneration !== this.ticket.sessionGeneration)
    ) {
      this.socket?.close(WEBSOCKET_GENERATION_MISMATCH_CLOSE_CODE, 'realtime generation changed');
      this.fail(new Error('The realtime generation changed.'));
      return;
    }
    switch (frame.type) {
      case 'ready':
        this.readyResolve?.();
        this.readyResolve = undefined;
        this.readyReject = undefined;
        break;
      case 'notification': {
        const notifications = isRecord(frame.notifications) ? frame.notifications : undefined;
        if (notifications !== undefined && typeof notifications.cursor === 'number') {
          this.socket?.send(
            JSON.stringify({
              type: 'cursor-ack',
              streamGeneration: this.hello.streamGeneration,
              sessionGeneration: this.ticket.sessionGeneration,
              cursor: notifications.cursor,
            }),
          );
        }
        const waiter = this.notificationWaiter;
        if (waiter === undefined) {
          if (this.notifications.length >= 8) this.notifications.shift();
          this.notifications.push(frame);
        } else {
          this.notificationWaiter = undefined;
          waiter.cleanup();
          waiter.resolve(frame);
        }
        break;
      }
      case 'movement-ack':
      case 'movement-error': {
        if (typeof frame.sequence !== 'number') return;
        const pending = this.movements.get(frame.sequence);
        if (pending === undefined) return;
        this.movements.delete(frame.sequence);
        pending.cleanup();
        const details =
          isRecord(frame.error) && isRecord(frame.error.details) ? frame.error.details : undefined;
        const recovery =
          details !== undefined && isRecord(details.movementRecovery)
            ? details.movementRecovery
            : undefined;
        if (
          recovery !== undefined &&
          (recovery.villageId !== this.hello.villageId ||
            recovery.sessionGeneration !== this.ticket.sessionGeneration)
        ) {
          pending.reject(new Error('The movement recovery belongs to a different session.'));
        } else if (frame.type === 'movement-error')
          // Older world sockets omit the envelope version on errors. The socket
          // has already negotiated nord-world-v2; preserve their typed rejection.
          pending.reject(
            new WorldRealtimeResponseError({ protocolVersion: BROWSER_PROTOCOL_VERSION, ...frame }),
          );
        else pending.resolve(frame);
        break;
      }
      case 'resync-required':
      case 'village-handoff':
      case 'server-closing':
        this.fail(new Error(`The realtime server requested ${frame.type}.`));
        break;
      case 'visitor-kick': {
        if (
          frame.sourceVillageId !== this.hello.villageId ||
          typeof frame.villageId !== 'number' ||
          !Number.isSafeInteger(frame.villageId) ||
          frame.villageId < 1 ||
          typeof frame.sessionGeneration !== 'number' ||
          !Number.isSafeInteger(frame.sessionGeneration) ||
          frame.sessionGeneration < this.ticket.sessionGeneration
        ) {
          this.fail(new Error('The realtime server sent an invalid visitor kick.'));
          break;
        }
        this.socket?.close(1000, 'visitor kicked');
        this.fail(new WorldRealtimeVisitorKickError(frame.sourceVillageId, frame.villageId));
        break;
      }
      default:
        this.fail(new Error('The realtime server sent an unsupported frame.'));
    }
  }

  private fail(reason: unknown): void {
    const error =
      reason instanceof Error
        ? reason
        : new Error('The realtime connection failed.', { cause: reason });
    if (!this.closed) this.closed = true;
    this.failureReason ??= error;
    this.readyReject?.(error);
    this.readyResolve = undefined;
    this.readyReject = undefined;
    if (this.notificationWaiter !== undefined) {
      this.notificationWaiter.cleanup();
      this.notificationWaiter.reject(error);
      this.notificationWaiter = undefined;
    }
    for (const pending of this.movements.values()) {
      pending.cleanup();
      pending.reject(error);
    }
    this.movements.clear();
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
